import React, { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { WorkflowModel, WorkflowAction } from 'winslow-core';
import './WorkflowDiagram.css';

interface WorkflowDiagramProps {
  workflowData: string; // JSON string of WorkflowModel array
}

const WorkflowDiagram: React.FC<WorkflowDiagramProps> = ({ workflowData }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Convert WorkflowModel JSON to React Flow nodes and edges
  const convertToReactFlow = useCallback((workflowModels: WorkflowModel[]) => {
    console.log('convertToReactFlow: Input workflow models:', workflowModels);
    if (!workflowModels || workflowModels.length === 0) {
      console.log('convertToReactFlow: No workflow models provided');
      return { nodes: [], edges: [] };
    }

    // Create nodes with positions
    const nodeMap = new Map<string, { x: number; y: number }>();
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Calculate positions using a simple layout algorithm
    // Filter out END nodes from visualization
    const filteredModels = workflowModels.filter(model => model.action !== WorkflowAction.END);
    const endNodeIds = new Set(workflowModels.filter(model => model.action === WorkflowAction.END).map(model => model.id));
    
    const startNodes = filteredModels.filter(model => model.action === WorkflowAction.START);
    const taskNodes = filteredModels.filter(model => model.action === WorkflowAction.TASK);
    const approvalNodes = filteredModels.filter(model => model.action === WorkflowAction.APPROVAL);
    const notificationNodes = filteredModels.filter(model => model.action === WorkflowAction.NOTIFICATION);

    let yOffset = 0;
    const xSpacing = 300;
    const ySpacing = 150;

    // Position start nodes
    startNodes.forEach((model, index) => {
      nodeMap.set(model.id, { x: 100, y: yOffset + index * ySpacing });
    });
    yOffset += startNodes.length * ySpacing + 100;

    // Position task nodes
    taskNodes.forEach((model, index) => {
      const row = Math.floor(index / 3);
      const col = index % 3;
      nodeMap.set(model.id, { 
        x: 400 + col * xSpacing, 
        y: yOffset + row * ySpacing 
      });
    });
    yOffset += Math.ceil(taskNodes.length / 3) * ySpacing + 100;

    // Position approval nodes
    approvalNodes.forEach((model, index) => {
      const row = Math.floor(index / 2);
      const col = index % 2;
      nodeMap.set(model.id, { 
        x: 400 + col * xSpacing, 
        y: yOffset + row * ySpacing 
      });
    });
    yOffset += Math.ceil(approvalNodes.length / 2) * ySpacing + 100;

    // Position notification nodes
    notificationNodes.forEach((model, index) => {
      const row = Math.floor(index / 2);
      const col = index % 2;
      nodeMap.set(model.id, { 
        x: 400 + col * xSpacing, 
        y: yOffset + row * ySpacing 
      });
    });

    // Create nodes (excluding END nodes)
    filteredModels.forEach((model) => {
      const position = nodeMap.get(model.id) || { x: 0, y: 0 };
      
      nodes.push({
        id: model.id,
        type: 'workflowNode',
        position,
        data: {
          label: model.label,
          action: model.action,
          owner: model.owner,
        },
        // Add handles for proper edge connections
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
    });

    // Create edges (excluding END nodes and edges pointing to END nodes)
    filteredModels.forEach((model) => {
      console.log('Processing model for edges:', model.id, 'onComplete:', model.onComplete, 'onFail:', model.onFail);
      console.log('Full model object:', model);
      
      // Handle different possible property names for connections
      const onComplete = model.onComplete || (model as any).on_complete || (model as any).next || (model as any).success;
      const onFail = model.onFail || (model as any).on_fail || (model as any).failure || (model as any).error;
      
      console.log('Resolved connections - onComplete:', onComplete, 'onFail:', onFail);
      
      // Get all node IDs for validation (excluding END nodes)
      const nodeIds = nodes.map(n => n.id);
      console.log('Available node IDs:', nodeIds);
      
      // Filter out edges that point to END nodes
      const validOnComplete = onComplete && !endNodeIds.has(onComplete) ? onComplete : null;
      const validOnFail = onFail && !endNodeIds.has(onFail) ? onFail : null;
      
      // Check if completion and fail nodes are the same (and both valid)
      const completionAndFailAreSame = validOnComplete && validOnFail && validOnComplete === validOnFail && nodeIds.includes(validOnComplete);
      const isStartNode = model.action === WorkflowAction.START;
      
      if (completionAndFailAreSame) {
        // Only draw one edge when completion and fail point to the same node
        const edge = {
          id: `${model.id}-next`,
          source: model.id,
          target: validOnComplete,
          type: 'smoothstep',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#6b7280',
          },
          style: {
            stroke: '#6b7280',
            strokeWidth: 2,
          },
        };
        console.log('Adding single edge (completion and fail are same):', edge);
        edges.push(edge);
      } else {
        // Draw separate edges for completion and fail
        if (validOnComplete && nodeIds.includes(validOnComplete)) {
          const edge = {
            id: `${model.id}-complete`,
            source: model.id,
            target: validOnComplete,
            ...(isStartNode ? {} : { label: 'Success' }),
            type: 'smoothstep',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#10b981',
            },
            style: {
              stroke: '#10b981',
              strokeWidth: 2,
            },
          };
          console.log('Adding success edge:', edge);
          edges.push(edge);
        }

        // Don't draw fail edges for START nodes
        if (validOnFail && nodeIds.includes(validOnFail) && !isStartNode) {
          const edge = {
            id: `${model.id}-fail`,
            source: model.id,
            target: validOnFail,
            label: 'Failure',
            type: 'smoothstep',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#ef4444',
            },
            style: {
              stroke: '#ef4444',
              strokeWidth: 2,
            },
          };
          console.log('Adding failure edge:', edge);
          edges.push(edge);
        }
      }
    });
    
    console.log('Total edges created:', edges.length);
    
    // If no edges were created from the model data, create simple sequential edges
    if (edges.length === 0 && nodes.length > 1) {
      console.log('No edges found in model data, creating sequential edges');
      for (let i = 0; i < nodes.length - 1; i++) {
        const edge = {
          id: `sequential-${i}`,
          source: nodes[i].id,
          target: nodes[i + 1].id,
          label: 'Next',
          type: 'smoothstep',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#6b7280',
          },
          style: {
            stroke: '#6b7280',
            strokeWidth: 2,
          },
        };
        edges.push(edge);
        console.log('Added sequential edge:', edge);
      }
      console.log('Created', edges.length, 'sequential edges');
    }

    return { nodes, edges };
  }, []);

  // Parse workflow data and convert to React Flow format
  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    console.log('WorkflowDiagram: Parsing workflow data:', workflowData);
    try {
      const parsed = JSON.parse(workflowData || '[]');
      console.log('WorkflowDiagram: Parsed data:', parsed);
      
      // Handle different possible data structures
      let workflowModels: WorkflowModel[] = [];
      
      if (Array.isArray(parsed)) {
        // Direct array of WorkflowModel objects
        workflowModels = parsed as WorkflowModel[];
      } else if (parsed && typeof parsed === 'object') {
        // Check if it's an object with a nodes/workflow property
        if (parsed.nodes && Array.isArray(parsed.nodes)) {
          workflowModels = parsed.nodes as WorkflowModel[];
        } else if (parsed.workflow && Array.isArray(parsed.workflow)) {
          workflowModels = parsed.workflow as WorkflowModel[];
        } else if (parsed.steps && Array.isArray(parsed.steps)) {
          workflowModels = parsed.steps as WorkflowModel[];
        } else {
          // Try to convert single object to array
          workflowModels = [parsed as WorkflowModel];
        }
      } else {
        console.error('WorkflowDiagram: Parsed data is not an array or object:', typeof parsed, parsed);
        return { nodes: [], edges: [] };
      }
      console.log('WorkflowDiagram: Parsed workflow models:', workflowModels);
      const result = convertToReactFlow(workflowModels);
      console.log('WorkflowDiagram: Converted to React Flow:', result);
      return result;
    } catch (error) {
      console.error('Error parsing workflow data:', error);
      return { nodes: [], edges: [] };
    }
  }, [workflowData, convertToReactFlow]);

  // Update nodes and edges when data changes
  React.useEffect(() => {
    console.log('WorkflowDiagram: Setting nodes and edges:', { 
      nodes: flowNodes.length, 
      edges: flowEdges.length,
      nodeIds: flowNodes.map(n => n.id),
      edgeSources: flowEdges.map(e => e.source),
      edgeTargets: flowEdges.map(e => e.target)
    });
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  // Custom node component
  const WorkflowNode = ({ data }: { data: any }) => {
    const getNodeStyle = (action: WorkflowAction) => {
      switch (action) {
        case WorkflowAction.START:
          return {
            background: '#10b981',
            color: 'white',
            border: '2px solid #059669',
          };
        case WorkflowAction.END:
          return {
            background: '#10b981',
            color: 'white',
            border: '2px solid #059669',
          };
        case WorkflowAction.TASK:
          return {
            background: '#3b82f6',
            color: 'white',
            border: '2px solid #2563eb',
          };
        case WorkflowAction.APPROVAL:
          return {
            background: '#f59e0b',
            color: 'white',
            border: '2px solid #d97706',
          };
        case WorkflowAction.NOTIFICATION:
          return {
            background: '#8b5cf6',
            color: 'white',
            border: '2px solid #7c3aed',
          };
        default:
          return {
            background: '#6b7280',
            color: 'white',
            border: '2px solid #4b5563',
          };
      }
    };

    const nodeStyle = getNodeStyle(data.action);

    return (
      <div className="workflow-node" style={nodeStyle}>
        <Handle
          type="target"
          position={Position.Left}
          style={{ background: '#555' }}
        />
        <div className="workflow-node-label">{data.label}</div>
        {data.owner && (
          <div className="workflow-node-owner">Owner: {data.owner}</div>
        )}
        <div className="workflow-node-action">{data.action}</div>
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: '#555' }}
        />
      </div>
    );
  };

  const nodeTypes = {
    workflowNode: WorkflowNode,
  };

  console.log('WorkflowDiagram: Rendering with nodes:', nodes.length, 'edges:', edges.length);

  if (!workflowData || workflowData.trim() === '') {
    console.log('WorkflowDiagram: No workflow data available');
    return (
      <div className="workflow-diagram-empty">
        <p>No workflow data available. Generate a workflow first.</p>
      </div>
    );
  }

  if (nodes.length === 0) {
    console.log('WorkflowDiagram: No nodes to render');
    return (
      <div className="workflow-diagram-empty">
        <p>No workflow nodes found in the data.</p>
      </div>
    );
  }

  return (
    <div className="workflow-diagram-container">
      <div style={{ position: 'absolute', top: 10, left: 10, background: 'white', padding: '5px', fontSize: '12px', zIndex: 1000 }}>
        Nodes: {nodes.length}, Edges: {edges.length}
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        attributionPosition="bottom-left"
      >
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

export default WorkflowDiagram;
